"use client";

import type { ChangeEvent } from "react";
import type { MotionValue } from "motion/react";
import type { Segment, TranscriptionResponse } from "@ui/components/demos/STT/types";

import { useMotionValue } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNativeAudio } from "@/app/_providers/native-audio/NativeAudioProvider";
import { transcribeSttFile } from "@/app/_api/transcribeSttFile";
import { captureEvent } from "@/app/_analytics/analytics";
import { createVideoThumbnail } from "@/app/_utilities/videoUtils";
import { logger } from "@utilities/logger";
import { fetchSTTSampleTranscript } from "@ui/components/demos/STT/STTServerFn";
import { convertSampleResponseToSegments, MEDIA_CONTENT_TYPES } from "@ui/components/demos/STT/utils";
import { EventName } from "@mw/shared/analyticsEventNames";
import type { Media } from "@mw/shared/payload-types";

export type UploadFn = (file: File) => Promise<TranscriptionResponse>;

export type STTHeroDisplayMode = "default" | "videoPreview";

export type UseSTTHeroPlaybackOptions = {
  uploadFn?: UploadFn;
};

export const defaultUploadFn: UploadFn = async file => {
  const [error, result] = await transcribeSttFile({ file, diarize: true });
  if (error || !result) throw new Error(error ?? "transcribe_failed");
  return result;
};

export interface STTHeroSample {
  id: string;
  name: string;
  audioUrl: string;
  sttResponse?: TranscriptionResponse | string;
  segments?: Segment[];
  thumbnail?: Media | null;
}

export function getSttHeroContentAnalyticsProps(uploadFileName: string | null, selectedIndex: number) {
  if (uploadFileName !== null) {
    return { content_type: "user_upload" as const };
  }
  return { content_type: "example" as const, example_index: selectedIndex };
}

export interface STTHeroPlaybackState {
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  segments: Segment[];
  isPlaying: boolean;
  isLoading: boolean;
  isUploading: boolean;
  uploadFileName: string | null;
  uploadedTranscription: TranscriptionResponse | null;
  uploadedThumbnailUrl: string | null;
  currentTime: MotionValue<number>;
  togglePlay: () => void;
  stop: () => void;
  seekToTime: (time: number) => void;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDownload: () => void;
  reportTranscriptionDownload: (download_format: string) => void;
  showUploadQuotaExceeded: boolean;
  uploadedMediaKind: "audio" | "video" | null;
}

export function useSTTHeroPlayback(
  samples: STTHeroSample[],
  { uploadFn = defaultUploadFn }: UseSTTHeroPlaybackOptions = {},
): STTHeroPlaybackState {
  const [selectedIndex, setSelectedIndexState] = useState(0);
  const [loadedSegments, setLoadedSegments] = useState<Record<string, Segment[]>>({});
  const [loadingSampleId, setLoadingSampleId] = useState<string | null>(null);
  const [uploadedSegments, setUploadedSegments] = useState<Segment[] | null>(null);
  const [uploadedTranscription, setUploadedTranscription] = useState<TranscriptionResponse | null>(null);
  const [uploadedThumbnailUrl, setUploadedThumbnailUrl] = useState<string | null>(null);
  const [uploadedAudioUrl, setUploadedAudioUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [uploadedMediaKind, setUploadedMediaKind] = useState<"audio" | "video" | null>(null);
  const [showUploadQuotaExceeded, setShowUploadQuotaExceeded] = useState(false);
  /** Tracks completion of `fetchSTTSampleTranscript` per sample so we can skeleton until first attempt finishes (including empty CMS responses). */
  const [transcriptFetchCompletedBySampleId, setTranscriptFetchCompletedBySampleId] = useState<Record<string, boolean>>(
    {},
  );

  const currentTime = useMotionValue(0);
  const loadingSampleIdRef = useRef<string | null>(null);
  const uploadedAudioUrlRef = useRef<string | null>(null);
  const uploadRequestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      if (uploadedAudioUrlRef.current) URL.revokeObjectURL(uploadedAudioUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (samples[0]) loadSegmentsIfNeeded(samples[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { togglePlay: audioTogglePlay, playingId, seek, stop: audioStop } = useNativeAudio();

  const currentSample = samples[selectedIndex];
  const audioId = `stt-hero-${currentSample?.id ?? selectedIndex}`;
  const isPlaying = playingId === audioId;

  const segments = useMemo(() => {
    if (uploadedSegments) return uploadedSegments;
    if (currentSample?.segments?.length) return currentSample.segments;
    if (loadedSegments[currentSample?.id]) return loadedSegments[currentSample.id];
    if (currentSample?.sttResponse) {
      try {
        const parsed: TranscriptionResponse =
          typeof currentSample.sttResponse === "string"
            ? JSON.parse(currentSample.sttResponse)
            : currentSample.sttResponse;
        return convertSampleResponseToSegments(parsed);
      } catch {
        return [];
      }
    }
    return [];
  }, [currentSample, loadedSegments, uploadedSegments]);

  const loadSegmentsIfNeeded = useCallback(
    async (sample: STTHeroSample) => {
      if (sample.segments?.length || sample.sttResponse || loadedSegments[sample.id]) return;

      loadingSampleIdRef.current = sample.id;
      setLoadingSampleId(sample.id);
      try {
        const transcript = await fetchSTTSampleTranscript(sample.id);
        if (transcript?.segments) {
          setLoadedSegments(prev => ({ ...prev, [sample.id]: transcript.segments }));
        }
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "[useSTTHeroPlayback] fetchSTTSampleTranscript failed");
      } finally {
        if (loadingSampleIdRef.current === sample.id) {
          loadingSampleIdRef.current = null;
          setLoadingSampleId(null);
        }
        setTranscriptFetchCompletedBySampleId(prev => ({ ...prev, [sample.id]: true }));
      }
    },
    [loadedSegments],
  );

  const setSelectedIndex = useCallback(
    (index: number) => {
      audioStop();
      currentTime.set(0);
      setShowUploadQuotaExceeded(false);
      setUploadedSegments(null);
      setUploadedTranscription(null);
      setUploadedThumbnailUrl(null);
      uploadRequestIdRef.current += 1;
      if (uploadedAudioUrlRef.current) {
        URL.revokeObjectURL(uploadedAudioUrlRef.current);
        uploadedAudioUrlRef.current = null;
      }
      setUploadedAudioUrl(null);
      setUploadFileName(null);
      setUploadedMediaKind(null);
      setSelectedIndexState(index);
      if (samples[index]) {
        loadSegmentsIfNeeded(samples[index]);
      }
    },
    [audioStop, currentTime, samples, loadSegmentsIfNeeded],
  );

  const handleTimeUpdate = useCallback(
    (time: number) => {
      currentTime.set(time);
    },
    [currentTime],
  );

  const handleEnded = useCallback(() => {
    currentTime.set(0);
  }, [currentTime]);

  const activeAudioUrl = uploadedAudioUrl ?? currentSample?.audioUrl;

  const togglePlay = useCallback(() => {
    if (!activeAudioUrl) return;
    if (!isPlaying) {
      captureEvent(EventName.mw_stt_example_played, getSttHeroContentAnalyticsProps(uploadFileName, selectedIndex));
    }
    audioTogglePlay({
      id: audioId,
      url: activeAudioUrl,
      onTimeUpdate: handleTimeUpdate,
      onEnded: handleEnded,
      trackingProps: { source: "stt_hero_demo", sample_name: currentSample?.name },
    });
  }, [
    audioTogglePlay,
    audioId,
    activeAudioUrl,
    currentSample?.name,
    handleTimeUpdate,
    handleEnded,
    isPlaying,
    uploadFileName,
    selectedIndex,
  ]);

  const stop = useCallback(() => {
    audioStop();
    currentTime.set(0);
  }, [audioStop, currentTime]);

  const seekToTime = useCallback(
    (time: number) => {
      seek(time);
      if (!isPlaying && activeAudioUrl) {
        captureEvent(EventName.mw_stt_example_played, getSttHeroContentAnalyticsProps(uploadFileName, selectedIndex));
        audioTogglePlay({
          id: audioId,
          url: activeAudioUrl,
          startTime: time,
          onTimeUpdate: handleTimeUpdate,
          onEnded: handleEnded,
          trackingProps: { source: "stt_hero_demo", sample_name: currentSample?.name },
        });
      }
    },
    [
      seek,
      isPlaying,
      audioTogglePlay,
      audioId,
      activeAudioUrl,
      currentSample?.name,
      handleTimeUpdate,
      handleEnded,
      uploadFileName,
      selectedIndex,
    ],
  );

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!MEDIA_CONTENT_TYPES.includes(file.type)) {
        logger.warn(`[useSTTHeroPlayback] Unsupported file type: ${file.type}`);
        return;
      }

      setShowUploadQuotaExceeded(false);

      const isVideoUpload = file.type.startsWith("video/");
      setUploadedMediaKind(isVideoUpload ? "video" : "audio");
      setUploadedThumbnailUrl(null);
      uploadRequestIdRef.current += 1;
      const uploadRequestId = uploadRequestIdRef.current;

      if (uploadedAudioUrlRef.current) URL.revokeObjectURL(uploadedAudioUrlRef.current);
      const objectUrl = URL.createObjectURL(file);
      uploadedAudioUrlRef.current = objectUrl;
      setUploadedAudioUrl(objectUrl);
      setUploadFileName(file.name);
      setIsUploading(true);

      audioStop();
      currentTime.set(0);

      if (isVideoUpload) {
        void createVideoThumbnail(file)
          .then(thumbnailUrl => {
            if (uploadRequestIdRef.current !== uploadRequestId) return;
            setUploadedThumbnailUrl(thumbnailUrl);
          })
          .catch(error => {
            logger.error(
              error instanceof Error ? error : null,
              "[useSTTHeroPlayback] Failed to generate uploaded video thumbnail",
            );
          });
      }

      captureEvent(EventName.mw_stt_transcribe_file, { source: "stt_hero" });

      try {
        const transcription = await uploadFn(file);
        setUploadedTranscription(transcription);
        setUploadedSegments(convertSampleResponseToSegments(transcription));
      } catch (error) {
        logger.error(error instanceof Error ? error : null, "[useSTTHeroPlayback] File upload transcription failed");
        const errorStatus = error instanceof Error ? error.message : "transcribe_failed";
        captureEvent(EventName.mw_stt_transcribe_file_error, {
          source: "stt_hero",
          error_status: errorStatus,
        });
        setUploadedTranscription(null);
        setUploadedSegments([]);
        if (errorStatus === "quota_exceeded") {
          captureEvent(EventName.mw_stt_quota_exceeded, { source: "stt_hero" });
          setShowUploadQuotaExceeded(true);
        }
      } finally {
        setIsUploading(false);
      }

      // Reset the file input so the same file can be re-selected
      e.target.value = "";
    },
    [audioStop, currentTime, uploadFn],
  );

  const exampleNeedsFetchedTranscript =
    !!currentSample && uploadFileName === null && !currentSample.segments?.length && !currentSample.sttResponse;

  const isLoading =
    segments.length === 0 &&
    exampleNeedsFetchedTranscript &&
    currentSample != null &&
    (!transcriptFetchCompletedBySampleId[currentSample.id] || loadingSampleId === currentSample.id);

  const reportTranscriptionDownload = useCallback(
    (download_format: string) => {
      captureEvent(EventName.mw_stt_transcription_download, {
        ...getSttHeroContentAnalyticsProps(uploadFileName, selectedIndex),
        download_format,
      });
    },
    [uploadFileName, selectedIndex],
  );

  const handleDownload = useCallback(() => {
    if (!segments.length) return;
    const text = segments.map(seg => seg.text.trim()).join("\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const baseName = uploadFileName ? uploadFileName.replace(/\.[^/.]+$/, "") : (currentSample?.name ?? "transcript");
    a.download = `${baseName}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    reportTranscriptionDownload("txt");
  }, [segments, currentSample?.name, uploadFileName, reportTranscriptionDownload]);

  return {
    selectedIndex,
    setSelectedIndex,
    segments,
    isPlaying,
    isLoading,
    isUploading,
    uploadFileName,
    uploadedTranscription,
    uploadedThumbnailUrl,
    currentTime,
    togglePlay,
    stop,
    seekToTime,
    handleFileChange,
    handleDownload,
    reportTranscriptionDownload,
    showUploadQuotaExceeded,
    uploadedMediaKind,
  };
}
